import React from "react";
import { Box, Button, Stack, TextField } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { type UseFormReturn, useWatch } from "react-hook-form";
import { type ResearchRequestFormData } from "../types";
import { CLabeledCheckbox } from "./CLabeledCheckbox";
import { RelatedFilesSection } from "./RelatedFilesSection";
import { FieldLabel, HelpText, inputSx, SectionCard } from "./formLayout";

interface LoginUser {
  name: string;
  email: string;
}

interface RequesterDetailsStepProps {
  form: UseFormReturn<ResearchRequestFormData>;
  files: File[];
  onFilesChange: (files: File[]) => void;
  loginUser: LoginUser;
}

export const RequesterDetailsStep: React.FC<RequesterDetailsStepProps> = ({
  form,
  files,
  onFilesChange,
  loginUser,
}) => {
  const { control, register, setValue, formState } = form;
  const needsPostalDelivery = useWatch({
    control,
    name: "needsPostalDelivery",
  });
  const notes = useWatch({ control, name: "notes" });

  const textField = (
    name: "requesterName" | "requesterEmail" | "requesterPhone",
    label: string,
    type: string,
    placeholder: string
  ): React.ReactNode => (
    <Box>
      <FieldLabel required>{label}</FieldLabel>
      <TextField
        {...register(name)}
        type={type}
        placeholder={placeholder}
        size="small"
        fullWidth
        inputProps={{ "aria-label": label }}
        error={Boolean(formState.errors[name])}
        helperText={formState.errors[name]?.message}
        sx={inputSx}
      />
    </Box>
  );

  return (
    <Stack spacing={2}>
      <RelatedFilesSection files={files} onFilesChange={onFilesChange} />

      <SectionCard
        title="依頼者情報"
        description="ログイン情報には電話番号がないため、電話番号は別途入力してください。"
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonIcon />}
            onClick={() => {
              setValue("requesterName", loginUser.name, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue("requesterEmail", loginUser.email, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            ログイン情報を反映
          </Button>
        }
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 1.5,
          }}
        >
          {textField("requesterName", "担当者名", "text", "例：山田 太郎")}
          {textField(
            "requesterEmail",
            "担当者メールアドレス",
            "email",
            "例：taro@example.com"
          )}
          {textField(
            "requesterPhone",
            "担当者電話番号",
            "tel",
            "例：03-1234-5678"
          )}
        </Box>
      </SectionCard>

      <SectionCard title="郵送">
        <CLabeledCheckbox
          control={control}
          name="needsPostalDelivery"
          label="郵送を希望する"
        />
        <HelpText>郵送希望時はレターパック代430円を概算へ加算します。</HelpText>
        {needsPostalDelivery ? (
          <Box sx={{ mt: 1.5 }}>
            <FieldLabel required>郵送先</FieldLabel>
            <TextField
              {...register("postalAddress")}
              placeholder="例：東京都千代田区丸の内1-1-1 ○○ビル 3F"
              size="small"
              fullWidth
              inputProps={{ "aria-label": "郵送先" }}
              error={Boolean(formState.errors.postalAddress)}
              helperText={formState.errors.postalAddress?.message}
              sx={inputSx}
            />
          </Box>
        ) : null}
      </SectionCard>

      <SectionCard title="その他調査上の補足・留意事項等">
        <TextField
          {...register("notes")}
          placeholder="調査で気をつけてほしい点などがあれば記入してください"
          multiline
          minRows={4}
          inputProps={{ "aria-label": "補足・留意事項", maxLength: 1000 }}
          error={Boolean(formState.errors.notes)}
          helperText={
            formState.errors.notes?.message ?? `${notes.length}/1,000文字`
          }
          fullWidth
          sx={inputSx}
        />
      </SectionCard>
    </Stack>
  );
};
